/* CT Parts Counter — search runtime.
   Working model 2026-08-07. Two jobs:
   1. Results page: renders /search results into [data-ct-results] from the
      baked catalog via CT.search — the baked index is the single source of
      what's searchable (merchandised products + gated service parts; parts
      are search-only since 10/8, never in grids/nav), so the theme never
      renders {{ search.results }}.
   2. Predictive: enhances every [data-ct-predictive] mount on any page
      (header, homepage, 404) with a keyboard-navigable dropdown.
   Cards built here match the generated-card contract in
   snippets/ct-grid-*.liquid byte-for-structure, so shared delegated
   handlers in ct-core.js (ATC, qty steppers) just work. */
(function () {
  'use strict';

  var PHONE_HTML = '<a href="tel:8649737973">864-973-7973</a>';
  /* Popular chips stay merchandised-only: parts are search-only (10/8),
     findable by typing but never promoted. */
  var POPULAR = [
    { label: 'Panel Pens', q: 'panel+pens' },
    { label: 'Panel Remote', q: 'panel+remote' },
    { label: 'Cameras', q: 'camera' },
    { label: 'Screen cleaning', q: 'cleaning' },
    { label: 'Wi-Fi module', q: 'wifi+module' }
  ];

  function esc(s) {
    return String(s === null || s === undefined ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---------- query normalization ----------
     CT.search matches the haystack as-is, so "wifi" misses "Wi-Fi".
     When the plain query yields 0 hits, retry with hyphen-stripped
     ("wi-fi" → "wifi") and hyphen-inserted ("wifi"/"wi fi" → "wi-fi")
     variants and merge unique hits, preserving order. */
  function searchSmart(q, limit) {
    if (!window.CT || !CT.search) return Promise.resolve([]);
    return CT.search(q, limit).then(function (hits) {
      if (hits.length) return hits;
      var variants = [];
      var stripped = q.replace(/(\w)-(\w)/g, '$1$2');
      if (stripped !== q) variants.push(stripped);
      var inserted = q.replace(/wi[\s-]?fi/gi, 'wi-fi');
      if (inserted !== q && variants.indexOf(inserted) === -1) variants.push(inserted);
      if (!variants.length) return hits;
      return Promise.all(variants.map(function (v) { return CT.search(v, limit); }))
        .then(function (lists) {
          var seen = {};
          var merged = [];
          lists.forEach(function (list) {
            list.forEach(function (p) {
              if (!seen[p.handle]) { seen[p.handle] = true; merged.push(p); }
            });
          });
          return merged.slice(0, limit);
        });
    });
  }

  /* ---------- card builder — mirrors the generated-card contract ---------- */
  function chipsHtml(models) {
    if (!models || !models.length) return '<span class="ct-chips" aria-hidden="true"></span>';
    var html = '<span class="ct-chips">';
    models.slice(0, 3).forEach(function (m) {
      html += '<span class="ct-chip">' + esc(m) + '</span>';
    });
    if (models.length > 3) html += '<span class="ct-chip ct-chip--more">+' + (models.length - 3) + '</span>';
    return html + '</span>';
  }

  function cardHtml(p) {
    var hasCompare = !!(p.compareAt && p.compareAt > p.price);
    var discounted = (p.flags || []).indexOf('discounted') !== -1 || hasCompare;
    var soldout = p.available === false;
    var cls = 'ct-card' + (discounted ? ' is-discounted' : '') + (soldout ? ' is-soldout' : '');

    var attrs = ' data-handle="' + esc(p.handle) + '"' +
      ' data-sub="' + esc(p.subSlug || '') + '"' +
      ' data-models="' + esc((p.models || []).join('|')) + '"' +
      ' data-family="' + esc((p.families || []).join('|')) + '"' +
      ' data-flags="' + esc((p.flags || []).join('|')) + '"' +
      ' data-price="' + Math.round((p.price || 0) * 100) + '"' +
      ' data-orders="' + (p.orders12 || 0) + '"' +
      ' data-title="' + esc((p.title || '').toLowerCase()) + '"';

    /* v4: the sale-red "Save $X" badge chip rides the plate ONLY where a
       real compare-at exists (the badge carries the save; the price line
       below carries the labeled anchor) — mirrors generate_model.py.
       Savings are FLOORED whole dollars (CT.moneyFloor), never rounded up. */
    var badge = hasCompare
      ? '<span class="ct-badge ct-num">Save ' + CT.moneyFloor(p.compareAt - p.price) + '</span>'
      : '';
    var plate = p.img
      ? '<span class="ct-plate">' + badge + '<img src="' + esc(p.img) + '" alt="' + esc(p.title) + '" loading="lazy"></span>'
      : '<span class="ct-plate ct-plate--empty">' + badge + '<span class="ct-plate__pn">' + esc(p.sku || p.title) + '</span><span class="ct-plate__hint">Photo pending</span></span>';

    var pn = p.sku
      ? '<span class="ct-card__pn" title="' + esc(p.sku) + '">' + esc(p.sku) + '</span>'
      : '<span class="ct-card__pn" aria-hidden="true"></span>';

    /* v4 price register: now price + labeled struck anchor. No red save
       text here — the plate badge carries the save. */
    var price = '<span class="ct-card__price"><span class="ct-price ct-num">' + CT.money(p.price) + '</span>';
    if (hasCompare) {
      /* v4 anchor register: whole dollars unless the price has real cents —
         mirrors money_anchor() in generate_model.py */
      var anchor = (p.compareAt % 1 > 0.004 && p.compareAt % 1 < 0.996) ? CT.money(p.compareAt) : CT.moneyShort(p.compareAt);
      /* used-unit contract: a used unit was never sold NEW at the anchor —
         "Sold at" for condition Used; mirrors card_html() in generate_model.py */
      var anchorLabel = p.condition === 'Used' ? 'Sold at' : 'Sold new at';
      price += '<span class="ct-price-was ct-price-was--labeled ct-num">' + anchorLabel + ' <s>' + anchor + '</s></span>';
    }
    price += '</span>';

    var unit = (p.pack && p.pack.perUnit)
      ? '<span class="ct-card__unit ct-num">' + CT.money(p.pack.perUnit) + ' per ' + esc(p.pack.unit) + '</span>'
      : '<span class="ct-card__unit" aria-hidden="true"></span>';

    var tail = '';
    if (soldout) {
      tail = p.bucket === 'Closeout'
        ? '<span class="ct-soldout-note">Sold — closeout stock is one and done</span>'
        : '<span class="ct-soldout-note">Out of stock</span>';
    } else if (p.bucket === 'Accessories') {
      tail = '<div class="ct-card__action" data-ct-atc-scope>' +
        '<span class="ct-qty">' +
        '<button type="button" data-dir="down" aria-label="Decrease quantity">&minus;</button>' +
        '<input type="number" inputmode="numeric" value="1" min="1" aria-label="Quantity">' +
        '<button type="button" data-dir="up" aria-label="Increase quantity">+</button>' +
        '</span>' +
        '<button type="button" class="ct-btn ct-btn--sm" data-ct-atc' +
        (p.variantId ? ' data-variant-id="' + esc(p.variantId) + '"' : '') +
        ' data-title="' + esc(p.title) + '">Add to cart</button>' +
        '</div>';
    }

    return '<article class="' + cls + '"' + attrs + '>' +
      '<a class="ct-card__link" href="/products/' + esc(p.handle) + '">' +
      plate +
      '<span class="ct-card__body">' +
      '<span class="ct-card__title" title="' + esc(p.title) + '">' + esc(p.title) + '</span>' +
      pn +
      chipsHtml(p.models) +
      '</span>' +
      '</a>' +
      price +
      unit +
      tail +
      '</article>';
  }

  /* ---------- empty state / popular searches ---------- */
  function popularHtml() {
    var links = POPULAR.map(function (s) {
      return '<a class="ct-search-popular__link" href="/search?q=' + s.q + '">' + esc(s.label) + '</a>';
    }).join('');
    return '<div class="ct-search-popular">' +
      '<span class="ct-micro">Popular searches</span>' +
      '<div class="ct-search-popular__links">' + links + '</div>' +
      '</div>';
  }

  function emptyStateHtml(terms) {
    return '<div class="ct-search-empty">' +
      '<p class="ct-h3">No match for “' + esc(terms) + '”.</p>' +
      '<p class="ct-search-empty__body ct-secondary">Try a product name or panel model — or call ' + PHONE_HTML + ' for anything you can’t find.</p>' +
      popularHtml() +
      '</div>';
  }

  /* Warranty-gate escape hatch (Phase 0, 8/13): a part-number query that
     matches a gated service part must not read as broken search — name the
     support path instead of a silent zero. No product link, ever.
     NEW COPY — pending client approval. */
  function gatedStateHtml(skus) {
    var lead = skus.length === 1
      ? 'Part <span class="ct-num">' + esc(skus[0]) + '</span> is a service part.'
      : 'That part number belongs to a service part.';
    return '<div class="ct-search-empty" data-ct-gated>' +
      '<p class="ct-h3">That part is available through Clear Touch Support.</p>' +
      '<p class="ct-search-empty__body ct-secondary">' + lead +
      ' To keep your panel’s warranty intact, it’s installed by a certified technician with Clear Touch Support guidance. Call ' + PHONE_HTML + ' — Mon–Fri, 8:30–5:30 ET — to order it.</p>' +
      '</div>';
  }

  /* Shape-based hatch (8/19 — parts never listed): a query that LOOKS like a
     part number but matches neither the merchandised catalog nor the gated
     SKU table still gets the support path, not a dead zero. Heuristic: ≥ 6
     digits once normalized, or a CTI- prefix — panel models like "7000XT"
     (4 digits) must NOT trigger it. NEW COPY — pending client approval. */
  function partNumberShaped(q) {
    var digits = String(q || '').replace(/\D/g, '');
    return digits.length >= 6 || /cti[ .-]?\d/i.test(String(q || ''));
  }

  function partsSupportHtml() {
    return '<div class="ct-search-empty" data-ct-parts-support>' +
      '<p class="ct-h3">Looking for a replacement part?</p>' +
      '<p class="ct-search-empty__body ct-secondary">Service parts are supplied directly by Clear Touch Support — installing them with a certified technician keeps your panel’s warranty intact. Call ' + PHONE_HTML + ' — Mon–Fri, 8:30–5:30 ET.</p>' +
      '</div>';
  }

  /* ---------- results page ---------- */
  function initResults() {
    var mount = document.querySelector('[data-ct-results]');
    if (!mount) return;
    var terms = (mount.getAttribute('data-terms') || '').trim();
    if (!terms) {
      try { terms = (new URLSearchParams(window.location.search).get('q') || '').trim(); } catch (e) { /* no-op */ }
    }
    if (!terms) {
      mount.innerHTML = '<div class="ct-search-empty">' + popularHtml() + '</div>';
      return;
    }
    searchSmart(terms, 50).then(function (hits) {
      /* The server title reflects Shopify's 8-product index ("Search: 0 results
         found") — retitle from the baked search so the tab matches the page. */
      var label = hits.length === 1 ? 'result' : 'results';
      document.title = hits.length + ' ' + label + ' for “' + terms + '” — Clear Touch';
      if (!hits.length) {
        if (window.CT.gatedLookup) {
          CT.gatedLookup(terms).then(function (g) {
            if (g) {
              document.title = 'Available through Clear Touch Support — Clear Touch';
              mount.innerHTML = gatedStateHtml(g.skus);
            } else if (partNumberShaped(terms)) {
              /* no exact gated SKU, but the query is part-number-shaped —
                 route to support (parts are never listed, 8/19) */
              document.title = 'Available through Clear Touch Support — Clear Touch';
              mount.innerHTML = partsSupportHtml();
            } else {
              mount.innerHTML = emptyStateHtml(terms);
            }
          });
        } else {
          mount.innerHTML = partNumberShaped(terms) ? partsSupportHtml() : emptyStateHtml(terms);
        }
        return;
      }
      mount.innerHTML =
        '<p class="ct-search-count ct-secondary"><span class="ct-num">' + hits.length + '</span> ' + label + ' for “' + esc(terms) + '”</p>' +
        '<div class="ct-grid' + (hits.length <= 2 ? ' ct-grid--few' : '') + '" data-ct-grid>' + hits.map(cardHtml).join('') + '</div>';
    }).catch(function () {
      mount.innerHTML = '<div class="ct-search-empty">' +
        '<p class="ct-h3">Search is unavailable right now.</p>' +
        '<p class="ct-search-empty__body ct-secondary">Please try again, or call ' + PHONE_HTML + ' — Mon–Fri, 8:30–5:30 ET.</p>' +
        '</div>';
    });
  }

  /* ---------- predictive dropdown ---------- */
  var mounts = [];

  function enhancePredictive(wrap) {
    if (!wrap || wrap.getAttribute('data-ct-predictive-bound')) return;
    var input = wrap.querySelector('input[type="search"], input[type="text"]');
    if (!input) return;
    wrap.setAttribute('data-ct-predictive-bound', 'true');
    if (window.getComputedStyle(wrap).position === 'static') wrap.style.position = 'relative';
    input.setAttribute('autocomplete', 'off');
    input.setAttribute('aria-expanded', 'false');

    var box = null;
    var timer = null;
    var token = 0;

    function close() {
      if (box && box.parentNode) box.parentNode.removeChild(box);
      box = null;
      input.setAttribute('aria-expanded', 'false');
    }

    function items() {
      return box ? Array.prototype.slice.call(box.querySelectorAll('.ct-predictive__item')) : [];
    }

    function activeIndex(list) {
      for (var i = 0; i < list.length; i++) {
        if (list[i].classList.contains('is-active')) return i;
      }
      return -1;
    }

    function setActive(list, i) {
      list.forEach(function (el, j) { el.classList.toggle('is-active', j === i); });
      if (i >= 0 && list[i].scrollIntoView) list[i].scrollIntoView({ block: 'nearest' });
    }

    function render(q, hits, gated) {
      close();
      box = document.createElement('div');
      box.className = 'ct-predictive';
      var html = '';
      if (!hits.length && gated) {
        /* gated part number recognized — support CTA, no product link
           (NEW COPY — pending client approval) */
        html = '<a class="ct-predictive__item" href="tel:8649737973" data-ct-gated>' +
          '<div>' +
          '<div class="ct-predictive__title">Available through Clear Touch Support</div>' +
          '<div class="ct-predictive__pn">Service part — call 864-973-7973 to order</div>' +
          '</div></a>';
      } else if (!hits.length && partNumberShaped(q)) {
        /* part-number-shaped but unrecognized — still the support path
           (parts are never listed, 8/19; NEW COPY — pending client approval) */
        html = '<div class="ct-predictive__empty">Looking for a replacement part? Service parts are supplied directly by Clear Touch Support — installing them with a certified technician keeps your panel’s warranty intact. Call ' + PHONE_HTML + ' — Mon–Fri, 8:30–5:30 ET.</div>';
      } else if (!hits.length) {
        html = '<div class="ct-predictive__empty">No match. Try a product name or panel model — or call ' + PHONE_HTML + '.</div>';
      } else {
        hits.forEach(function (p) {
          html += '<a class="ct-predictive__item" href="/products/' + esc(p.handle) + '">' +
            '<span class="ct-predictive__thumb">' + (p.img ? '<img src="' + esc(p.img) + '" alt="" loading="lazy">' : '') + '</span>' +
            '<div>' +
            '<div class="ct-predictive__title">' + esc(p.title) + '</div>' +
            (p.sku ? '<div class="ct-predictive__pn ct-num">' + esc(p.sku) + '</div>' : '') +
            '</div>' +
            '<span class="ct-predictive__price ct-num">' + CT.money(p.price) + '</span>' +
            '</a>';
        });
        html += '<a class="ct-predictive__item" href="/search?q=' + encodeURIComponent(q) + '">' +
          '<span class="ct-predictive__title" style="color:var(--ct-accent)">View all results for “' + esc(q) + '” →</span>' +
          '</a>';
      }
      box.innerHTML = html;
      wrap.appendChild(box);
      input.setAttribute('aria-expanded', 'true');
    }

    function run() {
      var q = input.value.trim();
      if (q.length < 2) { close(); return; }
      var t = ++token;
      searchSmart(q, 6).then(function (hits) {
        if (t !== token) return;
        if (!wrap.contains(document.activeElement)) return; /* focus moved on — stay closed */
        if (!hits.length && window.CT && CT.gatedLookup) {
          /* zero merchandised hits — check the gated part-number table */
          CT.gatedLookup(q).then(function (g) {
            if (t !== token) return;
            if (!wrap.contains(document.activeElement)) return;
            render(q, hits, g);
          });
          return;
        }
        render(q, hits);
      }).catch(function () { /* catalog unavailable — leave the plain form working */ });
    }

    input.addEventListener('input', function () {
      clearTimeout(timer);
      timer = setTimeout(run, 130);
    });

    input.addEventListener('focus', function () {
      if (!box && input.value.trim().length >= 2) run();
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { close(); return; }
      if (!box) return;
      var list = items();
      if (!list.length) return;
      var i = activeIndex(list);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive(list, (i + 1) % list.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive(list, i <= 0 ? list.length - 1 : i - 1);
      } else if (e.key === 'Enter') {
        if (i >= 0) {
          e.preventDefault();
          window.location.href = list[i].href;
        }
        /* no active row → let the form submit to /search?q=… */
      }
    });

    mounts.push({ wrap: wrap, close: close });

    /* if the user is already typing in this mount (lazy focusin bind), catch up */
    if (document.activeElement === input && input.value.trim().length >= 2) run();
  }

  /* click-outside closes every open dropdown */
  document.addEventListener('click', function (e) {
    mounts.forEach(function (m) {
      if (!m.wrap.contains(e.target)) m.close();
    });
  });

  /* late-injected mounts (e.g. the 404 catalog router) bind on first focus */
  document.addEventListener('focusin', function (e) {
    var wrap = e.target && e.target.closest ? e.target.closest('[data-ct-predictive]') : null;
    if (wrap) enhancePredictive(wrap);
  });

  function enhanceAll(root) {
    Array.prototype.forEach.call((root || document).querySelectorAll('[data-ct-predictive]'), enhancePredictive);
  }

  /* re-scan hook for anything that injects a mount after load */
  window.CT = window.CT || {};
  window.CT.bindPredictive = enhanceAll;

  function init() {
    initResults();
    enhanceAll();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
